<template>
  <component :is="componentName" :breadcrumbs="breadcrumbs" :page="page" />
</template>

<script setup lang="ts">
import { getIndirectClientHeaders, seoHead } from '~/assets/seo'
import { pathWithoutHash, setCacheControlHeader } from '~/assets/util'

// Capture composables before any await (SSR request-isolation pattern).
const event = useRequestEvent()
const route = useRoute()
const pageStore = usePageStore()
const globalStore = useGlobalStore()
const tixStore = useTixStore()
const discoveryOverviewStore = useDiscoveryOverviewStore()
const eventOverviewStore = useEventOverviewStore()
const pressOverviewStore = usePressOverviewStore()
const searchStore = useSearchStore()
const seriesOverviewStore = useSeriesOverviewStore()

const fullPath = pathWithoutHash(route.fullPath)

const { data } = await useAsyncData(`catch-all;${fullPath}`, async () => {
  const [, globalSeo, page] = await Promise.all([
    globalStore.fetchNavigation(),
    globalStore.fetchSeo(),
    pageStore.fetchUri({ uri: decodeURI(fullPath), push: true, options: { headers: getIndirectClientHeaders({ req: event?.node.req }) } }),
  ])
  return { globalSeo, page }
})

if (!data.value?.page) {
  setCacheControlHeader(event, true)
  throw createError({ statusCode: 404, statusMessage: 'Page not found', fatal: true })
}

const { page, globalSeo } = data.value

if (page._redirect_) {
  setCacheControlHeader(event, true)
  await navigateTo(page._redirect_.to, { redirectCode: page._redirect_.type, external: true })
}

const pageType = page.__typename.endsWith('_Entry') ? page.__typename.slice(0, -6) : page.__typename

const tixEnvMap: Record<string, string> = {
  contentOverview_education: 'education',
  page_education: 'education',
  production_education: 'education',
}
tixStore.setTixEnv(tixEnvMap[pageType] || 'tix') // fallback to "normal" tix env

// prettier-ignore
const pageTypesWithCacheControl = [
  'campaign_default',
  'discovery_discovery', 'discoveryCollection_discoveryCollection', 'discoveryOverview_discoveryOverview', 'discoverySearchOverview_discoverySearchOverview',
  'educationProductionOverview_default',
  'event_default', 'eventOverview_eventOverview',
  'page_page', 'page_education',
  'press_default', 'pressOverview_pressOverview',
  'search_search',
  'series_series', 'seriesOverview_seriesOverview',
]
if (pageTypesWithCacheControl.includes(pageType)) {
  setCacheControlHeader(event)
}

/*
 * Perform initial fetch on some pages, later updates will happen whenever the search parameters are updated in the store.
 * Each case body is wrapped in a block: the `<script setup>` compiler rewrites a top-level `await` into a parenthesized
 * expression and only prefixes it with the semicolon that this semicolon-free code needs when it can see the preceding
 * statement. Its scope tracking follows block statements only, so a bare `case` body hides the `initialize()` call above
 * the `await` from that check and the two get parsed as one call (`initialize(...)(...)`).
 */
switch (pageType) {
  case 'discoveryCollection_discoveryCollection': {
    discoveryOverviewStore.initialize({ keyword: '', ...route.query, categories: [page.title] })
    await Promise.all([discoveryOverviewStore.fetchAllFacets(), discoveryOverviewStore.fetch()])
    break
  }

  case 'discoverySearchOverview_discoverySearchOverview': {
    discoveryOverviewStore.initialize(route.query)
    await Promise.all([discoveryOverviewStore.fetchAllFacets(), discoveryOverviewStore.fetch()])
    break
  }

  case 'eventOverview_eventOverview': {
    eventOverviewStore.initialize(route.query)
    await Promise.all([eventOverviewStore.fetchBanners(), eventOverviewStore.fetchFallbackBanners(), eventOverviewStore.fetchAllFacets(), eventOverviewStore.fetch()])
    break
  }

  case 'pressOverview_pressOverview': {
    pressOverviewStore.initialize(route.query)
    await pressOverviewStore.fetch()
    break
  }

  case 'search_search': {
    searchStore.initialize(route.query)
    await searchStore.fetch()
    break
  }

  case 'seriesOverview_seriesOverview': {
    seriesOverviewStore.initialize(route.query)
    await Promise.all([
      seriesOverviewStore.fetchBanners(),
      seriesOverviewStore.fetchFallbackBanners(),
      seriesOverviewStore.fetchHighlightedOrganizer(),
      seriesOverviewStore.fetchAllFacets(),
      seriesOverviewStore.fetch(),
    ])
    break
  }
}

const componentMap: Record<string, string> = {
  campaign_default: 'TemplateCampaignDetail',
  contentOverview_default: 'TemplateOverview',
  contentOverview_education: 'TemplateEducationOverview',
  discovery_discovery: 'TemplateDiscoveryDetail',
  discoveryCollection_discoveryCollection: 'TemplateDiscoveryCollectionDetail',
  discoveryOverview_discoveryOverview: 'TemplateDiscoveryOverview',
  discoverySearchOverview_discoverySearchOverview: 'TemplateDiscoverySearchOverview',
  educationProductionOverview_default: 'TemplateEducationProductionOverview',
  event_default: 'TemplateEventDetailV2',
  eventOverview_eventOverview: 'TemplateEventOverview',
  favoritePage_favoritePage: 'TemplateFavoritePage',
  page_education: 'TemplatePageDetail',
  page_page: 'TemplatePageDetail',
  press_default: 'TemplatePressDetail',
  pressOverview_pressOverview: 'TemplatePressOverview',
  production_education: 'TemplateEducationProductionDetail',
  recommendation_default: 'TemplateRecommendationDetail',
  recommendation_v2: 'TemplateRecommendationDetailV2',
  search_search: 'TemplateSearch',
  series_series: 'TemplateSeriesDetail',
  seriesOverview_seriesOverview: 'TemplateSeriesOverview',
}

// Add prefix "discovery" for discovery detail, with fallback to home for every other page type.
const breadcrumbPrefixMap: Record<string, { labelKey: string; url: string | undefined }[]> = {
  discovery_discovery: [{ labelKey: 'discovery-overview', url: globalStore.discoveryOverview?.url }],
  discoverySearchOverview_discoverySearchOverview: [{ labelKey: 'discovery-overview', url: globalStore.discoveryOverview?.url }],
  event_default: [{ labelKey: 'event-overview', url: globalStore.eventOverview?.url }],
  press_default: [{ labelKey: 'press-overview', url: globalStore.pressOverview?.url }],
  series_series: [{ labelKey: 'series-overview', url: globalStore.seriesOverview?.url }],
}

const { breadcrumbs } = setupBreadcrumbs(page, breadcrumbPrefixMap[pageType] || [])
const componentName = componentMap[pageType] || 'TemplateUnknownPage'

useHead(() => seoHead(page, globalSeo))
</script>
